import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Toast } from "primereact/toast";
import ConfimeaHeader from "./components/ConfimeaHeader";
import ConfimeaFooter from "./components/ConfimeaFooter";
import ConfimeaConfirmationLoading from "./components/ConfimeaConfirmationLoading";
import ConfimeaConfirmationError from "./components/ConfimeaConfirmationError";
import ConfimeaConfirmationHeader from "./components/ConfimeaConfirmationHeader";
import ConfimeaConfirmationDocuments from "./components/ConfimeaConfirmationDocuments";
import ConfimeaConfirmationUpload from "./components/ConfimeaConfirmationUpload";
import ConfimeaConfirmationActions from "./components/ConfimeaConfirmationActions";
import ConfimeaConfirmationSuccess from "./components/ConfimeaConfirmationSuccess";
import ConfimeaService from "../../service/ConfimeaService";
import ProductService from "../../service/ProductService";
import SubnetService from "../../service/SubnetService";
import {
  getStoredConfimeaAssociationRef,
  CONFIMEA_DEFAULT_REFEREE,
} from "../../constants/confimeaAssociation";
import { useToastContext } from "../../contexts/ToastContext/ToastContext";
import localeText from "../../locale";
import "./ConfimeaConfirmation.css";

const ConfimeaConfirmation = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [sendingDocument, setSendingDocument] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [documentCloudinaryId, setDocumentCloudinaryId] = useState(null);
  const [documentSent, setDocumentSent] = useState(false);
  const [associationDocumentUrl, setAssociationDocumentUrl] = useState(null);
  const [loadingAssociationDocument, setLoadingAssociationDocument] = useState(false);

  const toast = useRef(null);
  const { showErrorToast } = useToastContext();
  const confimeaService = useRef(new ConfimeaService()).current;
  const productService = useRef(new ProductService()).current;
  const subnetService = useRef(new SubnetService()).current;

  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get("username");
  const activationToken = searchParams.get("activationToken");
  const userDocumentRef =
    searchParams.get("userdocument")?.trim() ||
    searchParams.get("association")?.trim() ||
    getStoredConfimeaAssociationRef() ||
    null;
  const isCustomAssociationDocument =
    !!userDocumentRef && userDocumentRef !== CONFIMEA_DEFAULT_REFEREE;

  useEffect(() => {
    if (username && activationToken) {
      confirmEmail();
    } else {
      setError(localeText("confirmationMissingParams"));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!emailConfirmed || !userDocumentRef) return;

    let cancelled = false;

    async function loadAssociationDocument() {
      setLoadingAssociationDocument(true);
      try {
        const res = await subnetService.getPDFByUsername(userDocumentRef);
        if (
          !cancelled &&
          (res.messageCode === 200000 || res.messageCode === 200001) &&
          res.result?.documentAssociation
        ) {
          setAssociationDocumentUrl(res.result.documentAssociation);
        }
      } catch (e) {
        console.log(e);
      } finally {
        if (!cancelled) setLoadingAssociationDocument(false);
      }
    }

    loadAssociationDocument();
    return () => {
      cancelled = true;
    };
  }, [emailConfirmed, userDocumentRef, subnetService]);

  const confirmEmail = async () => {
    try {
      setConfirming(true);
      const res = await confimeaService.confirmEmail(username, activationToken);

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        setError(
          localeText("data")?.find((msg) => msg.code === res.messageCode)?.message ||
            localeText("confirmationError"),
        );
        setLoading(false);
        setConfirming(false);
      } else {
        setEmailConfirmed(true);
        setLoading(false);
        setConfirming(false);
      }
    } catch (e) {
      setError(localeText("confirmationError"));
      setLoading(false);
      setConfirming(false);
      console.log(e);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.files || [];
    const file = files[0] || null;
    if (!file) return;

    if (file.size / 1024 / 1024 > 10) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("fileSizeTooBig"),
        life: 6000,
      });
      return;
    }

    try {
      const res = await productService.postUploadFile(null, file);

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
      } else {
        const cloudinaryId =
          typeof res.result === "string" ? res.result : res.result?.id || res.result;
        setDocumentCloudinaryId(cloudinaryId);
        setUploadedFiles([{ name: file.name, size: file.size, cloudinaryId: cloudinaryId }]);
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("fileUploadedSuccessfully"),
          life: 3000,
        });
      }
    } catch (e) {
      showErrorToast({ code: 500000 });
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    if (newFiles.length === 0) {
      setDocumentCloudinaryId(null);
    }
  };

  const handleSendDocument = async () => {
    if (!documentCloudinaryId) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseUploadDocument"),
        life: 6000,
      });
      return;
    }

    try {
      setSendingDocument(true);
      const description = localeText("confirmationDocumentDescription");
      const res = await confimeaService.addDocument(username, description, documentCloudinaryId);

      if (res.messageCode !== 200000 && res.messageCode !== 200001) {
        showErrorToast({ code: res.messageCode });
        setSendingDocument(false);
      } else {
        setDocumentSent(true);
        setSendingDocument(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) {
      showErrorToast({ code: 500000 });
      setSendingDocument(false);
    }
  };

  if (loading || confirming) {
    return <ConfimeaConfirmationLoading />;
  }

  if (error) {
    return <ConfimeaConfirmationError error={error} />;
  }

  if (!emailConfirmed) {
    return null;
  }

  if (documentSent) {
    return (
      <div className="confimea-confirmation-page">
        <ConfimeaHeader />
        <div className="confimea-confirmation-container">
          <div className="confimea-confirmation-card">
            <ConfimeaConfirmationSuccess />
          </div>
        </div>
        <ConfimeaFooter />
      </div>
    );
  }

  return (
    <div className="confimea-confirmation-page">
      <Toast position="top-right" ref={toast} />
      <ConfimeaHeader />
      <div className="confimea-confirmation-container">
        <div className="confimea-confirmation-card">
          <ConfimeaConfirmationHeader />

          <div className="confimea-confirmation-content">
            <ConfimeaConfirmationDocuments
              documentUrl={associationDocumentUrl}
              loadingDocument={loadingAssociationDocument}
              isAssociationMode={isCustomAssociationDocument}
              onMissingDocument={() => {
                toast.current.show({
                  severity: "error",
                  summary: localeText("error"),
                  detail: localeText("associationDocumentUnavailable"),
                  life: 6000,
                });
              }}
            />

            <ConfimeaConfirmationUpload
              uploadedFiles={uploadedFiles}
              onFileUpload={handleFileUpload}
              onRemoveFile={handleRemoveFile}
              disabled={sendingDocument}
            />

            <ConfimeaConfirmationActions
              onSendDocument={handleSendDocument}
              sendingDocument={sendingDocument}
              documentCloudinaryId={documentCloudinaryId}
            />
          </div>
        </div>
      </div>
      <ConfimeaFooter />
    </div>
  );
};

export default ConfimeaConfirmation;
